// Brief 85 §4 — TimeEntryModal, moved out of App.tsx.
//
// MOVED, NOT REWRITTEN. The statement block below is byte-identical to the body
// of the te clause as it stood in App.tsx —
//   sha256 f9637d117051ce2b014b3c7e9d7c8e98d1da8998fe0c12186c6aedf622d1de95
// — indentation included, which is why it sits at the depth it had inside
// App(). Only the imports, the Props type and the signature are new. Do not
// reformat: the indentation IS the proof.
//
// Props are the UNION of the App-scope names the body actually read, from a
// TypeScript-checker walk rather than chosen. The dialog declares no hook (§0
// measured zero across all 21 blocks), so it moves as a component without any
// state ownership changing hands.

/* 166 — `import React` is here for `react/react-in-jsx-scope`, which the
   committed eslint config still enforces and which the ratchet counts once per
   JSX element (`scripts/lint/eslint-ratchet.js`: a file's warning count may
   never rise). This brief adds JSX to this file, so the rule fires on every new
   element; putting React in scope is the rule's OWN remedy rather than a way
   around it, it costs nothing at runtime (the automatic JSX transform does not
   need it), and it clears the same debt for every element already here. */
import React from "react";
import { NO_AUTOFILL, noAutofillClass } from "../../lib/noAutofill"; // 213B §18 — no sign-ins, no contacts, in a modal's field
import { useTranslation } from "../../i18n";
import { FS, RAD } from "../../lib/tokens";
import { ModalLayout } from "../../components/SharedUI";
import MatterPicker from "../MatterPicker"; // 131 §2
import { SK, fmin, fq, pname } from "../../utils"; // fq: 208 §10 — the derived rate, in the amount field's figure
import { tempId } from "../../lib/tempId"; // 207 §1.2
import { choosableCo } from "../../lib/collaboratorState";
import { dropTimerRow } from "../../lib/timerStops";
import { roleLabel } from "../../hooks/useRoleLabels";
import { isDividerCode } from "../../utils/taxonomyMap"; // 110 §3a
import type { TFn } from "../../lib/taxonomyTypes";
import type { makeModalFields } from "../modalFields";
import MenuSelect from "../MenuSelect"; // 154B §2
import AmountField from "../AmountField"; // 208 §10 — the one amount field

type Vocab = ReturnType<typeof makeModalFields>;

type Props = {
  _fl: Vocab["_fl"];
  _appIsAdmin: boolean;
  activeTimers: any[];
  addRecentCase: (userId: unknown, caseId: unknown) => void;
  cases: any[];
  cm: () => void;
  collaborators: any[];
  collectiveCases: any[];
  currentUser: any;
  form: any;
  getRecentCases: (u: unknown) => readonly unknown[];
  isSecRole: (c: unknown) => boolean;
  persist: (k: string, d: unknown, fn: unknown, opts?: unknown) => Promise<unknown>;
  sTeCaseQ: (v: unknown) => void;
  saveTask: (fd2: unknown) => boolean;
  setActiveTimers: (v: unknown) => void;
  setFocusedTimerIdx: (v: unknown) => void;
  setForm: (v: unknown) => void;
  setTimeEntries: (v: unknown) => void;
  setTkCompletePend: (v: unknown) => void;
  t: TFn;
  tasks: any[];
  teCaseQ: string;
  timeEntries: any[];
  tkCompletePend: any;
  tkTypes: any[];
  toast: (m?: string, type?: string, opts?: { action?: unknown }) => void;
  updateField: (k: string, v: unknown) => void;
};

export default function TimeEntryModal({
  _fl,
  _appIsAdmin,
  activeTimers,
  addRecentCase,
  cases,
  cm,
  collaborators,
  collectiveCases,
  currentUser,
  form,
  getRecentCases,
  isSecRole,
  persist,
  sTeCaseQ,
  saveTask,
  setActiveTimers,
  setFocusedTimerIdx,
  setForm,
  setTimeEntries,
  setTkCompletePend,
  t,
  tasks,
  teCaseQ,
  timeEntries,
  tkCompletePend,
  tkTypes,
  toast,
  updateField,
}: Props) {
  /* 157A §9 — the WRITTEN value needs every language, not just the current
     one, so the detector below can recognise a row whichever UI wrote it.
     `t` arrives as a prop and answers in the current language only; `tIn`
     answers in a NAMED one. */
  const { tIn } = useTranslation();
  /* 165 — THE FORM'S MATTER, READ ONCE AND TYPED. `form` arrives as `any`
     (nine prefill shapes feed this modal), and three places need the same
     answers off it: which id the picker shows, which LIST that id came out of
     (Case and CollectiveCase ids collide — 131 §6), and what "Même dossier"
     carries forward. Typed here, not cast at each site, so the new code pays
     its own eslint debt instead of raising the ratchet
     (`scripts/lint/eslint-baseline.json`, SESSION_RULES). */
  interface TeMatterFields {
    caId?: number | string | null;
    ccId?: number | string | null;
  }
  /** The previous form in a functional `setForm` — an object, never `any`. */
  type TeFormPrev = Record<string, unknown>;
  const teMatter = form as TeMatterFields;
  const teCaId = teMatter.caId ? Number(teMatter.caId) : null;
  const teCcId = teMatter.ccId ? Number(teMatter.ccId) : null;
  const teMatterId = teCaId ?? teCcId;
  /* Only consulted when `teMatterId` is non-null (MatterPicker answers "" for
     an absent id before it reads the kind, `MatterPicker.tsx:155`). */
  const teMatterKind = teCaId ? "ca" : "cc";
        const teToday = new Date().toISOString().slice(0, 10);
        const teFuture = (form.date || teToday) > teToday;
        const teSave = () => {
          if (teFuture) return false;
          /* C67 — self-only logging: a non-admin's NEW entry is forced onto
             the current user (no picker renders for them, below); on EDIT
             the row keeps its stored collaborator (non-admins cannot
             reassign ownership — mirrors the server rule). Admins keep the
             picker value. */
          const _teCoId =
            _appIsAdmin || form.id ? +form.coId : (currentUser ?? +form.coId);
          /* Round 15 (C91) — resolve the case ONCE, with a task fallback:
             a task-bound entry whose prefill lost the case adopts the bound
             task's caseId/ccId from the canonical row (the server's create
             does the same adoption — display hints are never persistence).
             Unbound manual logs still require an explicit case. */
          let _teCaId = form.ccId ? null : +form.caId || null;
          let _teCcId = form.ccId ? +form.ccId : null;
          const _teTaskBound =
            typeof form._fromTask === "number" && form._fromTask > 0;
          if (_teCaId == null && _teCcId == null) {
            const _tkb = _teTaskBound
              ? tasks.find((x: any) => x.id === form._fromTask)
              : null;
            if (_tkb && _tkb.caseId != null) _teCaId = _tkb.caseId;
            else if (_tkb && _tkb.ccId != null) _teCcId = _tkb.ccId;
          }
          /* Round 16 (C97) — a TASK-BOUND entry may save without a client-
             resolvable case: the taskId hint rides in the body and the
             server adopts the task's case (the viewer may simply be unable
             to SEE it — retracted delegation, expired grant). Manual,
             unbound logs still require an explicit case. */
          if (
            (!_teCaId && !_teCcId && !_teTaskBound) ||
            !_teCoId ||
            !(form.min > 0)
          )
            return false;
          const {
            _fromTask: _ft,
            _lockCo: _lc,
            _caseFixed: _cf,
            _restrictToAssigned: _ra,
            /* Round 14 (C87) — display-only hints for the locked case field,
               never persisted. */
            _caseTitle: _ct,
            _caseRef: _cr,
            /* 166 §H.3 — the DERIVED rate, handed over by the table so the
               rate box can show what it would be. A display hint like the two
               above: `pick()` drops it anyway (it is not in ALLOWED), and it
               is named here so the census in the §A comment stays complete. */
            _derivedRate: _dr,
            /* Round 17 (C102b) — the completion flow's timer marker: the
               save consumes (stops + clears) exactly that timer below. */
            _consumeTimer: _ctm,
            ...e2base
          } = {
            ...form,
            caId: _teCaId,
            ccId: _teCcId,
            coId: _teCoId,
            /* 175 §1 — the second half of the same rounding: the keystroke
               rounded and the save rounded again. What remains is the Int
               coercion the column needs, on the 5-minute grid no longer. */
            min: Math.round(+(form.min || 0)),
            mark: form.mark ?? "unmarked",
            /* Round 14 (C82b) — transient task binding for the server's
               auto-ongoing hook (TimeEntry persists no taskId column; the
               server reads it from the create body only). Subtask-bound
               entries resolve server-side via subtaskId either way.
               _fromTask can be the literal `true` (legacy "from a task"
               marker without an id) — only a real numeric id rides. */
            ...(typeof form._fromTask === "number" && form._fromTask > 0
              ? { taskId: form._fromTask }
              : {}),
          };
          delete e2base.hrs;
          const _teSavePromise = form.id
            ? persist(
                SK.te,
                timeEntries.map((t2: any) => (t2.id === form.id ? { ...t2, ...e2base } : t2)),
                setTimeEntries,
              )
            : persist(SK.te, [...timeEntries, { ...e2base, id: tempId() }], setTimeEntries);
          /* Phase 30 (D-3) — everything downstream gates on the SERVER
             outcome now: a failed POST no longer discards the held timer
             (it stays paused with its elapsed minutes so the log can be
             retried), no longer completes the task, and the "Logged" toast
             fires only after confirmation (the engine rolled back the row
             and toasted the error on failure). */
          _teSavePromise.then((r: any) => {
            if (!r.ok) {
              setTkCompletePend(null);
              return;
            }
            toast(
              form.id
                ? t("toast.updated", { fallback: "Updated" })
                : t("toast.logged", { fallback: "Logged" }),
            );
            if (_ft && tkCompletePend) {
              saveTask({ ...tkCompletePend, status: "completed" });
              setTkCompletePend(null);
            }
            /* Round 17 (C102b) — the logged entry consumes the completed
               entity's held timer: stop + clear, focus index clamped (the
               stopTimerAsk convention). */
            if (_ctm) {
              dropTimerRow(
                { setActiveTimers, setFocusedTimerIdx, count: activeTimers.length },
                _ctm.taskId,
                _ctm.subtaskId || null,
                // 116 §3 — the kind, so an event timer drops ITSELF and not
                // every other (null, null) row beside it.
                _ctm.eventId || null,
              );
            }
          });
          addRecentCase(currentUser, _teCaId || _teCcId);
          return true;
        };
        const teClose = () => {
          setTkCompletePend(null);
          cm();
        };
        return (
          <ModalLayout
            title={
              form.id
                ? t("modal.title.edit_time_entry", { fallback: "Edit time entry" })
                : t("modal.title.log_time", { fallback: "Add time entry" })
            }
            onClose={teClose}
            onSave={() => {
              // Phase 30 (D-3) — the toast moved into teSave's confirm path.
              if (teSave()) cm();
            }}
            /* 165 — THE FIVE VERBS RENDER IN BOTH MODES. The footer was
               gated `!form.id &&`, so an edit got ModalLayout's default
               Save/Cancel pair (`SharedUI.tsx:1277` renders it only when
               `footer` is falsy) and a new entry could not be started from an
               existing one. Laurent's ruling, 2026-09-04. `teSave()` already
               branches on `form.id` for update-vs-create (:187) and already
               toasts "Updated" vs "Logged" on the CONFIRMED save (:205), so
               the verbs needed no new save path — only the three corrections
               below. */
            footer={
              (
                <div className="modal-footer">
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 4,
                      marginBottom: 8,
                      ...(teFuture
                        ? { opacity: 0.4, pointerEvents: "none" }
                        : {}),
                    }}
                  >
                    <button
                      type="button"
                      className="modal-save"
                      style={{
                        padding: "8px 10px",
                        fontSize: FS.s11,
                        background: "rgba(52,211,153,.15)",
                        color: "var(--gn)",
                        border: "1px solid rgba(52,211,153,.3)",
                      }}
                      onClick={() => {
                        if (teSave()) {
                          /* 165 — MEASURED, and this is what edit mode adds:
                             the add-mode form is a small prefill, but an
                             EDIT form is the whole server row
                             (`CaseTimeEntriesSection.tsx:404` spreads it), so
                             `{...p}` carried three of its client-writable
                             identity columns (`timeEntries.js:119`) into the
                             CREATE body — proven through the server's own
                             pick(): invoiceId 88, emailGraphId and subtaskId
                             all rode. `emailGraphId` is half of
                             `@@unique([caseId, emailGraphId, collaboratorId])`
                             (`schema.prisma:983`), so cloning an email-derived
                             entry would collide; `invoiceId` would put the new
                             row on an already-issued invoice. "Tout pareil"
                             means the FORM's fields, never the row's identity.
                             Both spellings are cleared because pick() only
                             applies an alias when it is defined (`pick.js:39`)
                             — the canonical twin would otherwise survive
                             alone. */
                          setForm((p: TeFormPrev) => ({
                            ...p,
                            id: undefined,
                            desc: "",
                            invoiceId: undefined,
                            egid: undefined,
                            emailGraphId: undefined,
                            stId: undefined,
                            subtaskId: undefined,
                            /* 166 §A — AND THE TRANSIENT FLAGS GO WITH THE
                               IDENTITY. Censused: eight `_`-prefixed keys can
                               reach this form, and three of them are about
                               THIS SAVE rather than about the form or the
                               surface, so a clone that repeats them acts
                               twice on one thing.
                                 · `_fromTask` (App.tsx:4252,
                                   subtaskTiming.ts:110, CaseTaskRow.tsx:406,
                                   TaskPromptDialog.tsx:127, App.tsx:7019) is
                                   read at :182 into the create body's
                                   `taskId`, where the server's auto-ongoing
                                   hook acts on it — a second entry bound to
                                   the same task.
                                 · `_consumeTimer` (TaskPromptDialog.tsx:132,
                                   App.tsx:7021) is read at :195 into
                                   `dropTimerRow` — the same timer stopped
                                   twice.
                                 · `_fromSubtask` (App.tsx:4253, :7616,
                                   :17048, subtaskTiming.ts:113) — the eighth
                                   key, which the brief's list did not name.
                                   Nothing in src/ or server/src/ reads it and
                                   `pick()` drops it (absent from ALLOWED and
                                   from ALIASES, `timeEntries.js:119/:127`),
                                   so it is inert on the wire; it goes because
                                   it pairs with `subtaskId`, cleared two
                                   lines up, and a form that still claims a
                                   subtask origin it no longer has is a trap
                                   for whoever gives it a reader.
                               The other five STAY, because they describe the
                               form or the surface and are still true of the
                               next entry made in it: `_caseTitle`/`_caseRef`
                               (:519-520) name the matter the clone keeps and
                               are read only under `_caseFixed`;
                               `_restrictToAssigned` (:596) is the
                               spans-matters surface's own picker rule;
                               `_lockCo` (:617) and `_caseFixed` (:505) belong
                               to the opener, and after §A neither can arrive
                               on an edit at all. */
                            _fromTask: undefined,
                            _fromSubtask: undefined,
                            _consumeTimer: undefined,
                          }));
                          sTeCaseQ(null);
                        }
                      }}
                      title={t("modal.te.save_all_same_tip", {
                        fallback:
                          "Save, keep all fields, clear description only",
                      })}
                    >
                      {t("modal.te.save_all_same", {
                        fallback: "Save & All Same",
                      })}
                    </button>
                    <button
                      type="button"
                      className="modal-save"
                      style={{
                        padding: "8px 10px",
                        fontSize: FS.s11,
                        background: "rgba(74,124,255,.15)",
                        color: "var(--ac)",
                        border: "1px solid rgba(74,124,255,.3)",
                      }}
                      onClick={() => {
                        if (teSave()) {
                          setForm({
                            caId: form.caId,
                            /* 165 (3) — MEASURED: the shape kept caId only, so
                               a collective-case entry lost its matter
                               ENTIRELY — `teSave` reads the case off
                               `form.ccId` first (:134) and a CC row's caId is
                               null, so the next body went out
                               `{caseId: null, ccId: null}` and the guard at
                               :150 refused it. "Same case" has to mean the
                               same MATTER, of either kind. */
                            ccId: teMatter.ccId,
                            date:
                              form.date || new Date().toISOString().slice(0, 10),
                            coId: form.coId,
                            min: 0,
                          });
                          sTeCaseQ(null);
                        }
                      }}
                      title={t("modal.te.save_same_case_tip", {
                        fallback: "Save, keep Case and Date",
                      })}
                    >
                      {t("modal.te.save_same_case", {
                        fallback: "Save & Same Case",
                      })}
                    </button>
                    <button
                      type="button"
                      className="modal-save"
                      style={{
                        padding: "8px 10px",
                        fontSize: FS.s11,
                        background: "rgba(251,191,36,.15)",
                        color: "var(--am)",
                        border: "1px solid rgba(251,191,36,.3)",
                      }}
                      onClick={() => {
                        if (teSave()) {
                          setForm({
                            type: form.type,
                            min: form.min || 0,
                            date: new Date().toISOString().slice(0, 10),
                          });
                          sTeCaseQ(null);
                        }
                      }}
                      title={t("modal.te.save_same_activity_tip", {
                        fallback: "Save, keep Activity Type and Minutes",
                      })}
                    >
                      {t("modal.te.save_same_activity", {
                        fallback: "Save & Same Activity",
                      })}
                    </button>
                    <button
                      type="button"
                      className="modal-save"
                      style={{
                        padding: "8px 10px",
                        fontSize: FS.s11,
                        background: "rgba(248,113,113,.15)",
                        color: "var(--rd)",
                        border: "1px solid rgba(248,113,113,.3)",
                      }}
                      onClick={() => {
                        if (teSave()) {
                          setForm({
                            date: new Date().toISOString().slice(0, 10),
                            min: 0,
                          });
                          sTeCaseQ(null);
                        }
                      }}
                      title={t("modal.te.save_clear_tip", {
                        fallback: "Save and clear all fields",
                      })}
                    >
                      {t("modal.te.save_clear", { fallback: "Save & Clear" })}
                    </button>
                  </div>
                  <button type="button" className="modal-cancel" onClick={teClose}>
                    {t("modal.te.close_without_saving", {
                      fallback: "Close without Saving",
                    })}
                  </button>
                  <button
                    type="button"
                    className="modal-save"
                    style={{
                      ...(teFuture
                        ? { opacity: 0.4, pointerEvents: "none" }
                        : {}),
                    }}
                    onClick={() => {
                      if (teSave()) {
                        cm();
                      }
                    }}
                  >
                    {t("modal.te.save_exit", { fallback: "✓ Save & EXIT" })}
                  </button>
                </div>
              )
            }
          >
              <>
                {(() => {
                  /* ── 157A §9 — A BUTTON THAT WRITES STORED TEXT ──────────
                     The label was translated and the written value was the
                     raw English array element, so half the pair was
                     internationalised. Writing the translated label alone
                     would not fix it — the description is STORED, and the
                     detector matched the stored string, so a row written in
                     the French UI would not be recognised as prefixed when
                     reopened in Dutch and a second prefix would stack on the
                     first. And every row already in the firm carries the
                     ENGLISH word.

                     So: PREFIXES stays the stable internal KEY list, the
                     button WRITES the current language's label, and detection
                     runs against EVERY label in all three languages plus the
                     bare English key — that last one is the back-compatibility
                     leg for rows already in the database. Existing rows are
                     NOT migrated: recognising them is enough, and rewriting
                     stored descriptions is a data migration nobody asked for.

                     The `+ ": "` in every comparison is what keeps the Dutch
                     "Intern" from matching a French "Interne: …". */
                  const PREFIXES = ["client", "opponent", "internal"];
                  const labelOf = (k: string) =>
                    t("modal.te.prefix." + k, {
                      fallback: k.charAt(0).toUpperCase() + k.slice(1),
                    });
                  const formsOf = (k: string) => {
                    const out = new Set<string>();
                    // The historical stored form: the bare English key,
                    // capitalised, which is what every existing row carries.
                    out.add(k.charAt(0).toUpperCase() + k.slice(1));
                    for (const lng of ["en", "fr", "nl"]) {
                      const v = tIn(lng, "modal.te.prefix." + k);
                      if (v && typeof v === "string") out.add(v);
                    }
                    out.add(labelOf(k));
                    return [...out].filter(Boolean);
                  };
                  const hit = (() => {
                    const d = form.desc || "";
                    for (const k of PREFIXES) {
                      for (const f of formsOf(k)) {
                        if (d.startsWith(f + ": "))
                          return { key: k, rest: d.slice(f.length + 2) };
                        if (d === f) return { key: k, rest: "" };
                      }
                    }
                    return null;
                  })();
                  const curPrefix = hit ? hit.key : undefined;
                  const setPrefix = (k: any) => {
                    // Strip whatever form is actually stored, write the label
                    // for the language the user is looking at.
                    const stripped = hit ? hit.rest : form.desc || "";
                    const label = labelOf(k);
                    updateField(
                      "desc",
                      stripped ? label + ": " + stripped : label + ": ",
                    );
                  };
                  return (
                    <>
                      <div className="field-row">
                        {form._caseFixed ? (
                          (() => {
                            /* Round 14 (C87) — off-team viewers (delegated
                               rows) cannot resolve the case from their
                               server-scoped lists; the completion paths pass
                               the row-attached title/ref as display hints so
                               the locked field still names the case. */
                            const _fc =
                              (form.caId
                                ? cases.find((c: any) => c.id === +form.caId) ||
                                  collectiveCases.find((c: any) => c.id === +form.caId)
                                : form.ccId
                                  ? collectiveCases.find((c: any) => c.id === +form.ccId)
                                  : null) ||
                              (form._caseTitle
                                ? { title: form._caseTitle, ref: form._caseRef || "" }
                                : null);
                            return (
                              <div className="field">
                                <label>
                                  {t("field.case", { fallback: "Case" })}
                                </label>
                                <div
                                  style={{
                                    padding: "5px 8px",
                                    fontSize: FS.s11,
                                    background: "var(--bi)",
                                    border: "1px solid var(--br)",
                                    borderRadius: RAD.r6,
                                    color: "var(--ts)",
                                  }}
                                >
                                  {_fc ? (
                                    <>
                                      <span style={{ fontWeight: 600 }}>
                                        {_fc.title}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: FS.s10,
                                          color: "var(--tm)",
                                          marginLeft: 6,
                                          fontFamily:
                                            "'JetBrains Mono',monospace",
                                        }}
                                      >
                                        {_fc.ref}
                                      </span>
                                    </>
                                  ) : (
                                    t("field.case", { fallback: "Case" })
                                  )}
                                </div>
                              </div>
                            );
                          })()
                        ) : (
                          <div className="field">
                            <label>
                              {t("field.case_required", { fallback: "Case *" })}
                            </label>
                            <MatterPicker
                              t={t}
                              cases={cases}
                              collectiveCases={collectiveCases}
                              /* 165 — THE PICKER LEARNS `cc`, which is the
                                 house pattern everywhere else it offers both
                                 kinds (`TaskModal.tsx:242-248`,
                                 `BulkTimeEntryModal.tsx:174-191` — the bulk
                                 editor of these very rows). This one read and
                                 wrote `caId` ALONE while its option list
                                 merges cases AND collective cases
                                 (`MatterPicker.tsx:121-124`), so: a CC-bound
                                 form rendered an EMPTY field (a CC row's caId
                                 is null), and picking a collective case wrote
                                 its id into `caId` — a Case foreign key
                                 (`schema.prisma:967`), where the ids collide
                                 across the two tables (131 §6). §1's "Même
                                 dossier" now carries `ccId` forward, so
                                 without this the kept matter would be
                                 invisible and a pick over it silently
                                 ignored (`teSave` reads ccId first, :134). */
                              selectedId={teMatterId}
                              selectedKind={teMatterKind}
                              query={teCaseQ}
                              setQuery={sTeCaseQ}
                              placeholder={t("modal.te.search_case_ph", {
                                fallback: "Search case by title or ref…",
                              })}
                              getRecentCases={getRecentCases}
                              currentUser={currentUser}
                              restrictToAssigned={!!form._restrictToAssigned}
                              onClear={() =>
                                setForm((p: TeFormPrev) => ({
                                  ...p,
                                  caId: "",
                                  ccId: "",
                                }))
                              }
                              onPick={(m) => {
                                setForm((p: TeFormPrev) => ({
                                  ...p,
                                  caId: m._type === "ca" ? m.id : "",
                                  ccId: m._type === "cc" ? m.id : "",
                                }));
                                (
                                  document.activeElement as HTMLElement | null
                                )?.blur();
                              }}
                            />
                          </div>
                        )}
                        {form._lockCo || !_appIsAdmin ? (
                          (() => {
                            /* C67 — the collaborator picker is ADMIN-ONLY.
                               Non-admins see a locked display: themselves on
                               a new entry, the stored owner on an edit (they
                               cannot reassign — server enforces the same). */
                            const _lcId =
                              _appIsAdmin || form.id
                                ? +form.coId
                                : (currentUser ?? +form.coId);
                            const _lc = collaborators.find((c: any) => c.id === _lcId);
                            return _lc ? (
                              <div className="field">
                                <label>
                                  {t("field.collaborator", {
                                    fallback: "Collaborator",
                                  })}
                                </label>
                                <div
                                  style={{
                                    padding: "5px 8px",
                                    fontSize: FS.s11,
                                    background: "var(--bi)",
                                    border: "1px solid var(--br)",
                                    borderRadius: RAD.r6,
                                    color: "var(--ts)",
                                  }}
                                >
                                  {pname(_lc)}{" "}
                                  <span
                                    style={{ fontSize: FS.s10, color: "var(--tm)" }}
                                  >
                                    ({roleLabel(_lc.role)})
                                  </span>
                                </div>
                              </div>
                            ) : null;
                          })()
                        ) : (
                          <div className="field">
                            <label>
                              {t("field.collaborator_required", {
                                fallback: "Collaborator *",
                              })}
                            </label>
                            <MenuSelect
                              className="field-input"
                              value={form.coId ? String(form.coId) : ""}
                              onChange={(v) => updateField("coId", v)}
                              options={[
                                ["", t("common.select", { fallback: "Select" })] as const,
                                /* 75 §4 — a NEW time entry is chosen work, so
                                   archived people leave the list; the stored
                                   coId is kept so EDITING an archived person's
                                   existing entry cannot silently re-point it. */
                                ...choosableCo(
                                  collaborators,
                                  form.coId ? [+form.coId] : null,
                                ).map(
                                  (c: any) =>
                                    [
                                      String(c.id),
                                      `${pname(c)} (${roleLabel(c.role)})`,
                                    ] as const,
                                ),
                              ]}
                            />
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "auto 1fr",
                          gap: 8,
                        }}
                      >
                        <div className="field">
                          <label>{_fl("Date")}</label>
                          <input
                            className="field-input"
                            type="date"
                            max={teToday}
                            value={form.date || teToday}
                            onChange={(e) => updateField("date", e.target.value)}
                          />
                          {teFuture && (
                            <p
                              style={{
                                fontSize: FS.s9_5,
                                color: "var(--rd)",
                                margin: "3px 0 0",
                              }}
                            >
                              {t("modal.te.cannot_log_future", {
                                fallback: "Cannot log time for a future date",
                              })}
                            </p>
                          )}
                        </div>
                        <div className="field">
                          <label>
                            {t("field.minutes_required", {
                              fallback: "Minutes *",
                            })}
                            {form.min > 0 && (
                              <span
                                style={{
                                  fontWeight: 400,
                                  color: "var(--ts)",
                                  marginLeft: 6,
                                  textTransform: "none",
                                }}
                              >
                                = {fmin(form.min)}
                              </span>
                            )}
                          </label>
                          <input
                            className="field-input"
                            type="number"
                            step={1}
                            min={0}
                            value={form.min || 0}
                            /* 175 §1 — MEASURED: the single editor rounded the
                               same way the bulk one did, on the keystroke AND
                               again on save, so it takes the same treatment in
                               the same pass. Any whole minute, stored as
                               typed. */
                            onChange={(e) =>
                              updateField("min", +(e.target.value || 0))
                            }
                            autoFocus={!!form._fromTask}
                          />
                          <div
                            style={{
                              display: "flex",
                              gap: 4,
                              marginTop: 6,
                              alignItems: "center",
                            }}
                          >
                            {[5, 10, 15, 30, 60, 180].map((n) => (
                              <button
                                key={n}
                                type="button"
                                style={{
                                  fontSize: FS.s9_5,
                                  padding: "3px 8px",
                                  borderRadius: RAD.r12,
                                  background: "var(--bi)",
                                  border: "1px solid var(--br)",
                                  color: "var(--ts)",
                                  cursor: "pointer",
                                  fontFamily: "'JetBrains Mono',monospace",
                                  fontWeight: 600,
                                  flexShrink: 0,
                                }}
                                onClick={() => updateField("min", (+form.min || 0) + n)}
                              >
                                +{n < 60 ? n + "m" : n === 60 ? "1h" : "3h"}
                              </button>
                            ))}
                            {form.min > 0 && (
                              <button
                                type="button"
                                style={{
                                  fontSize: FS.s9_5,
                                  padding: "1px 6px",
                                  borderRadius: RAD.r12,
                                  background: "none",
                                  border: "1px solid rgba(248,113,113,.35)",
                                  color: "var(--rd)",
                                  cursor: "pointer",
                                  flexShrink: 0,
                                  fontFamily: "inherit",
                                  lineHeight: 1.4,
                                  marginLeft: "auto",
                                }}
                                onClick={() => updateField("min", 0)}
                              >
                                {t("common.reset", { fallback: "Reset" })}
                              </button>
                            )}
                          </div>
                        </div>
                        {/* ── 166 §H.3 — THE STATED HOURLY RATE ──────────────
                            Laurent's ruling 8, 2026-09-04: an entry may carry
                            a rate a person states, clearable back to the
                            derived one, and it is ONE field —
                            `TimeEntry.invoicedRate` — not a second column.

                            EMPTY IS THE CLEAR, and it is the whole-row PUT's
                            natural reading: this modal sends the whole entry,
                            so an empty box is "no override" and the server
                            writes null (`coerceFloatFields`,
                            timeEntries.js). A number is the rate.

                            THE PLACEHOLDER IS THE DERIVED RATE, and it comes
                            from the SERVER: `TimeEntryTable` asks
                            `entryFees.js` for `derived` beside `er` and hands
                            it over on the edit payload (`_derivedRate`). It is
                            deliberately not computed here — P17.1a F1 removed
                            the browser's own effR resolver precisely so there
                            would be one pricing chain, and a placeholder that
                            lied would be worse than none. On the ADD path
                            there is no row yet and so no hint; the field still
                            works.

                            Ruling 9's colour is on the VALUE, not the label:
                            a box holding a stated rate says so in `--st`, the
                            same token the table's cell uses. */}
                        <div className="field">
                          <label>
                            {t("field.hourly_rate", { fallback: "Hourly rate (€/h)" })}
                          </label>
                          {/* 208 §10 — the one amount field (src/components/AmountField.tsx): typed with a decimal comma, shown « 1 234,50 » */}
                          <AmountField
                            inputMode="decimal"
                            value={form.invoicedRate}
                            empty=""
                            placeholder={
                              form._derivedRate != null && +form._derivedRate > 0
                                ? fq(Math.round(+form._derivedRate * 100) / 100)
                                : t("te.rate.derived_ph", { fallback: "derived from the case" })
                            }
                            title={t("te.rate.stated_hint", {
                              fallback:
                                "Leave empty to use the case's hourly rate; a number here overrides it for this entry only.",
                            })}
                            style={form.invoicedRate != null && form.invoicedRate !== "" ? { color: "var(--st)" } : undefined}
                            onAmount={(n) =>
                              updateField("invoicedRate", n == null ? "" : String(n))
                            }
                          />
                        </div>
                      </div>
                      <div className="field">
                        <label>
                          {t("field.activity_type", {
                            fallback: "Activity Type",
                          })}
                        </label>
                        {/* 166 §B — THE SECOND SURFACE THE SAME VALUE REACHES,
                            and the brief's coordinate is the wrong component:
                            it named `FieldSelect.tsx:186-214`'s provisional
                            option, but THIS select is a `MenuSelect` (154B §2)
                            and composes no such row. MEASURED at
                            `MenuSelect.tsx:169-170`: `closedText = current ?
                            current[1] : value` — an unmatched stored value
                            already SHOWS ITSELF on the closed face rather than
                            falling to the first option. So the value was
                            never hidden here, only unmarked; the brief's
                            "a native <option> cannot be coloured, the CLOSED
                            select can" is right about where the mark goes, and
                            the closed face is exactly what `style` paints.

                            The rule is the call site's, not the primitive's:
                            `MenuSelect`'s own header says it must not learn
                            what a taxonomy is, and a generic "unmatched value
                            → stated colour" inside it would also repaint the
                            Billability select beside this one. Same question as
                            `TimeEntryTable`'s chip, same empty-vocabulary
                            guard, same existing hint key. */}
                        <MenuSelect
                          className="field-input"
                          value={form.type || ""}
                          onChange={(v) => updateField("type", v)}
                          {...(form.type &&
                          tkTypes.length > 0 &&
                          !tkTypes.some((ty: any) => ty.code === form.type)
                            ? {
                                style: { color: "var(--st)" },
                                title: t("taxonomy.orphan.hint", {
                                  fallback:
                                    "This entry no longer exists in the list — the value shown is what the record still holds. Pick a current one to relink it.",
                                }),
                              }
                            : {})}
                          options={[
                            [
                              "",
                              t("common.select_dashed", {
                                fallback: "— Select —",
                              }),
                            ] as const,
                            ...(() => {
                              const __cb = collaborators.find((x: any) => x.id === +form.coId);
                              /* 200 §E [RULED — Laurent: "for other verticals
                                 it should not be hard coded"] — the secretarial
                                 list is the TAXONOMY's: the activity codes the
                                 firm ticked « secretarial » in the editor
                                 (meta.secretarial, carried on each tkTypes row),
                                 where App's SEC_TASKS literal named five legal
                                 codes. [JUDGEMENT — advisor, stated]: a firm
                                 that has ticked NOTHING restricts nothing — a
                                 zero-billing role then sees the whole list,
                                 never an empty one (an empty list would stop a
                                 secretary logging any time at all, and the
                                 flag is opt-in). */
                              const __secPool = tkTypes.filter((ty: any) => ty.secretarial === true);
                              const __pool = isSecRole(__cb) && __secPool.length > 0
                                ? __secPool
                                : tkTypes;
                              /* 110 §3a's divider is 108 §6b's row now */
                              return __pool.map(
                                (ty: any) =>
                                  [
                                    String(ty.code),
                                    isDividerCode(ty.code)
                                      ? String(ty.desc || "")
                                      : `${ty.code} — ${ty.desc}`,
                                  ] as const,
                              );
                            })(),
                          ]}
                        />
                      </div>
                      <div className="field">
                        <label>
                          {t("te.col.billable", { fallback: "Billability" })}
                        </label>
                        <MenuSelect
                          className="field-input"
                          value={form.mark || "unmarked"}
                          onChange={(v) => updateField("mark", v)}
                          options={[
                            ["unmarked", t("te.mark.unmarked", { fallback: "Mark" })],
                            ["billable", t("te.mark.billable", { fallback: "Billable" })],
                            ["unbillable", t("te.mark.unbillable", { fallback: "Unbillable" })],
                          ]}
                        />
                      </div>
                      <div className="field">
                        <label>
                          {t("field.description", { fallback: "Description" })}
                        </label>
                        <div
                          style={{
                            display: "flex",
                            gap: 4,
                            marginBottom: 4,
                            alignItems: "center",
                          }}
                        >
                          {PREFIXES.map((p) => (
                            <button
                              key={p}
                              type="button"
                              style={{
                                fontSize: FS.s9_5,
                                padding: "3px 8px",
                                borderRadius: RAD.r10,
                                background:
                                  curPrefix === p ? "var(--as)" : "var(--bi)",
                                border:
                                  "1px solid " +
                                  (curPrefix === p ? "var(--ag)" : "var(--br)"),
                                color:
                                  curPrefix === p ? "var(--ac)" : "var(--ts)",
                                cursor: "pointer",
                                fontWeight: 600,
                                flexShrink: 0,
                              }}
                              onClick={() => setPrefix(p)}
                            >
                              {labelOf(p)}
                            </button>
                          ))}
                          {form.desc && (
                            <button
                              type="button"
                              style={{
                                fontSize: FS.s9_5,
                                padding: "1px 6px",
                                borderRadius: RAD.r12,
                                background: "none",
                                border: "1px solid rgba(248,113,113,.35)",
                                color: "var(--rd)",
                                cursor: "pointer",
                                flexShrink: 0,
                                fontFamily: "inherit",
                                lineHeight: 1.4,
                                marginLeft: "auto",
                              }}
                              onClick={() => updateField("desc", "")}
                            >
                              {t("common.clear", { fallback: "Clear" })}
                            </button>
                          )}
                        </div>
                        <textarea
                          {...NO_AUTOFILL}
                          className={noAutofillClass("field-input")}
                          rows={3}
                          value={form.desc || ""}
                          onChange={(e) => updateField("desc", e.target.value)}
                          style={{
                            resize: "vertical",
                            fontFamily: "inherit",
                            fontSize: FS.s11,
                          }}
                          placeholder={t("modal.te.describe_work_ph", {
                            fallback: "Describe the work done…",
                          })}
                        />
                      </div>
                    </>
                  );
                })()}{" "}
              </>
          </ModalLayout>
        );
}
